// Admin: users, roles, audit, logs, alerts and the cold log archive.
//
// Split across lifecycle namespaces on purpose. Users/roles are authoritative
// directory reads; logs and alerts are realtime because an operator watching
// them expects them to move. Archive listings are volatile but do not poll,
// while a content-addressed archive object is immutable.

import { api } from '@/api/client';
import { CADENCE, FRESHNESS, defineQuery } from '@/api/resource';
import { buildQs } from '@/api/query-string';
import type {
  AdminUserResponse,
  AuditListResponse,
  LogsResponse,
  RolesResponse,
  UsersListResponse,
  Alert,
  AlertRulesResponse,
  AlertsResponse,
  LogArchiveResponse,
  LogGroupsPage,
  LogStats,
  LogsPage,
  LlmUsageResponse,
  ProviderDriftResponse,
} from '@/api/types';
import type { AdminConfigWireResponse } from '@/api/config-types';
import type {
  NotificationChannelsResponse,
  RemediationCatalogResponse,
} from '@/api/types.gen';

export interface AdminUsersQuery {
  q?: string;
  role?: string;
  status?: string;
}

/**
 * What `GET /admin/provider-drift` reads. Wire names, because `buildQs`
 * enumerates the object and the server parses `since_ms`/`campaign_id`.
 *
 * `since_ms` is REQUIRED here even though the route treats it as optional: the
 * server's own 7-day default would open a cache slot (`{}`) that no screen ever
 * subscribes to, which is the `eventsList({})` defect restated. `lib/provider-
 * drift.ts` is the only thing that builds one, so the loader and the screen
 * cannot disagree about the window.
 */
export interface ProviderDriftQuery {
  view?: 'unresolved' | 'history';
  target?: string;
  lifecycle?: string;
  status?: string;
  cursor?: string;
  limit?: number;
  since_ms: number;
  campaign_id?: string;
}

export interface AuditQuery {
  actor_id?: string;
  target_type?: string;
  target_id?: string;
  from?: string;
  to?: string;
  window?: '24h' | '7d' | '30d' | '90d';
  cursor?: string;
  /**
   * Page size. The screen has always sent this — the type simply never said so,
   * which was invisible while the only caller went unchecked and became a
   * type error the moment a typed route loader had to reproduce the same key.
   */
  limit?: number;
}

/**
 * What `admin-logs.tsx` actually sends. Every field here is parsed by
 * `LogsQuery` in `api/src/routes/admin.rs` and forwarded to the query.
 *
 * `component`, `trace_id`, `signature` and `error_kind` were dropped when this
 * interface was written from the base's five-field version — the screen has
 * always sent all four (a trace drill-down sends `{ trace_id }` alone, and a
 * group drill-down `{ signature, window }`), and nothing caught it because that
 * screen was not type-checked then. Nothing broke at runtime: the
 * object is forwarded verbatim and `clean()` enumerates it, so the URL and the
 * cache key were always correct. What was lost was the ability to make a TYPED
 * call — `useAdminLogs({ trace_id })` failed excess-property checking.
 *
 * `route`, `http_status` and `limit` are on the wire and deliberately absent:
 * no caller sends them, and an interface listing fields nothing produces is how
 * the next reader learns to distrust it.
 */
export interface LogFilters {
  level?: string;
  source?: string;
  component?: string;
  trace_id?: string;
  /** Exact grouping signature generated at the durable log boundary. */
  signature?: string;
  /** Exact machine tag for the error class, e.g. `api_error`, `action_failed`. */
  error_kind?: string;
  q?: string;
  window?: string;
  cursor?: string;
}

/** Users per page of the directory walk (the server's maximum). */
const USERS_PAGE = 200;
/** Pages the directory walk may read before it refuses: 100 × 200 = 20,000 users. */
const USERS_MAX_PAGES = 100;

/**
 * Every user, never the server's default first page of 50. Admin → Users and
 * Admin → Roles search and count in the browser, so an unfetched page is an
 * account nobody can find or suspend. The route pages by offset over
 * `created_at DESC, id`, so the walk reads full pages until a short one, drops
 * any user a concurrent insert shifted onto a second page, and refuses loudly
 * rather than stopping early.
 */
export const adminUsers = defineQuery({
  namespace: 'authoritative',
  name: 'admin.users',
  domains: ['identity_rbac'],
  request: async (q: AdminUsersQuery, signal): Promise<UsersListResponse> => {
    const users: UsersListResponse['users'] = [];
    const seen = new Set<string>();
    for (let page = 0; page < USERS_MAX_PAGES; page += 1) {
      const response = await api.get<UsersListResponse>(
        `/admin/users${buildQs({ ...q, limit: USERS_PAGE, offset: page * USERS_PAGE })}`,
        { signal },
      );
      for (const user of response.users) {
        if (!seen.has(user.id)) {
          seen.add(user.id);
          users.push(user);
        }
      }
      if (response.users.length < USERS_PAGE) return { users };
    }
    throw new Error('The user directory is larger than the screen can load.');
  },
  unwrap: (r) => r.users,
});

export const adminUserById = defineQuery({
  namespace: 'authoritative',
  name: 'admin.userById',
  domains: ['identity_rbac'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<AdminUserResponse>(`/admin/users/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.user,
});

export const adminRoles = defineQuery({
  namespace: 'authoritative',
  name: 'admin.roles',
  domains: ['identity_rbac'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<RolesResponse>('/admin/roles', { signal }),
  unwrap: (r) => r.roles,
});

/**
 * The newest few audit events for one query, one request, never a walk: the
 * user drawer's "Recent activity" shows 8 rows, and walking a busy actor's
 * whole window to show them cost a request per 50 events.
 */
export const adminAuditRecent = defineQuery({
  namespace: 'authoritative',
  name: 'admin.auditRecent',
  volatile: true,
  request: (q: AuditQuery & { limit: number }, signal) =>
    api.get<AuditListResponse>(`/admin/audit${buildQs(q)}`, { signal }),
  unwrap: (r) => r.events,
});

/** Pages the audit walk may read before it refuses: 50 × 200 = 10,000 events. */
const AUDIT_MAX_PAGES = 50;

/**
 * Every audit event in the window, never a first page presented as the whole.
 * The screen filters and searches in the browser, so a page it never fetched
 * is an event nobody can find: on 2026-09-28 "Last 90 days" showed 200 of 247
 * because the cursor the server returned was dropped. The walk follows the
 * keyset cursor to the end, and refuses loudly rather than stopping early.
 */
export const adminAudit = defineQuery({
  namespace: 'authoritative',
  name: 'admin.audit',
  volatile: true,
  request: async (q: AuditQuery, signal): Promise<AuditListResponse> => {
    const events: AuditListResponse['events'] = [];
    let cursor: string | null = null;
    for (let page = 0; page < AUDIT_MAX_PAGES; page += 1) {
      const response: AuditListResponse = await api.get<AuditListResponse>(
        `/admin/audit${buildQs({ ...q, cursor: cursor ?? undefined })}`,
        { signal },
      );
      events.push(...response.events);
      cursor = response.cursor ?? null;
      if (!cursor) return { events, cursor: null };
    }
    throw new Error('The audit log has more events in this window than the screen can load; narrow the window.');
  },
  unwrap: (r) => r.events,
});

/**
 * The live log tail. `keyOf` strips empty values so a filter the operator
 * cleared does not open a second cache slot that means the same thing —
 * previously done by building a `clean` object in the hook, which meant the key
 * and the URL could in principle disagree about what "empty" was.
 */
export const adminLogs = defineQuery({
  namespace: 'realtime',
  name: 'admin.logs',
  volatile: true,
  keyOf: (filters: LogFilters) => [clean(filters)],
  cadence: CADENCE.fast,
  request: (filters: LogFilters, signal) =>
    api.get<LogsResponse>(`/admin/logs${buildQs(clean(filters))}`, { signal }),
});

function clean(filters: LogFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) if (value) out[key] = value;
  return out;
}

export const adminLogStats = defineQuery({
  namespace: 'realtime',
  name: 'admin.logStats',
  volatile: true,
  keyOf: (windowHours: number) => [windowHours],
  cadence: CADENCE.slow,
  request: (windowHours: number, signal) =>
    api.get<LogStats>(`/admin/log-stats?window_hours=${windowHours}`, { signal }),
});

export const adminLogGroups = defineQuery({
  namespace: 'realtime',
  name: 'admin.logGroups',
  volatile: true,
  keyOf: (input: { windowHours: number; level: string | null }) => [input.windowHours, input.level],
  cadence: CADENCE.slow,
  request: (input: { windowHours: number; level: string | null }, signal) =>
    api.get<LogGroupsPage>(
      `/admin/log-groups?window_hours=${input.windowHours}` +
        `${input.level ? `&level=${input.level}` : ''}`,
      { signal },
    ),
});

export const adminAlerts = defineQuery({
  namespace: 'realtime',
  name: 'admin.alerts',
  volatile: true,
  keyOf: (activeOnly: boolean) => [activeOnly],
  cadence: CADENCE.slow,
  refetchOnWindowFocus: true,
  request: (activeOnly: boolean, signal) =>
    api.get<AlertsResponse>(`/admin/alerts${activeOnly ? '?active=true' : ''}`, { signal }),
});

/**
 * One page of resolved alert history older than a list response's
 * `resolved_cursor`. The list itself carries every unresolved alert and the
 * newest resolved page; older history is read on request and never polled.
 */
export const adminAlertsResolvedPage = defineQuery({
  namespace: 'authoritative',
  name: 'admin.alertsResolvedPage',
  volatile: true,
  keyOf: (cursor: string) => [cursor],
  request: (cursor: string, signal) =>
    api.get<AlertsResponse>(`/admin/alerts?resolved_before=${encodeURIComponent(cursor)}`, { signal }),
});

/**
 * One incident selected by an alert-email deep link. It remains independent of
 * the status-filtered list so a resolved incident is still visible when the
 * Triggered tab opens on its normal Active filter.
 */
export const adminAlertById = defineQuery({
  namespace: 'realtime',
  name: 'admin.alertById',
  volatile: true,
  keyOf: (id: string) => [id],
  cadence: CADENCE.slow,
  refetchOnWindowFocus: true,
  // A missing linked incident is a stable, useful screen state. Do not hide it
  // behind the realtime namespace's delayed retry.
  retry: false,
  request: (id: string, signal) =>
    api.get<Alert>(`/admin/alerts/${encodeURIComponent(id)}`, { signal }),
});

/** Rules and channels change with the authoritative configuration revision. */
export const adminAlertRules = defineQuery({
  namespace: 'realtime',
  name: 'admin.alertRules',
  domains: ['configuration'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<AlertRulesResponse>('/admin/alert-rules', { signal }),
});

export const adminNotificationChannels = defineQuery({
  namespace: 'realtime',
  name: 'admin.channels',
  domains: ['configuration'],
  keyOf: () => [],
  request: (_: void, signal) =>
    api.get<NotificationChannelsResponse>('/admin/notification-channels', { signal }),
});

/**
 * The managed config document, its secrets metadata and per-node apply state.
 *
 * The freshness is `live` and focus refetch is on **because the editor's
 * concurrent-edit banner compares the served `desired_version` against the
 * version the draft was based on**. Under a namespace default that let this go
 * stale, the banner was unreachable and a second administrator's save came back
 * as a bare 409.
 *
 * The cadence is a function of the payload: watch closely while an apply is in
 * flight, drop back to a background tick once it settles.
 */
export const adminConfig = defineQuery({
  namespace: 'realtime',
  name: 'admin.config',
  volatile: true,
  keyOf: () => [],
  freshness: FRESHNESS.live,
  refetchOnWindowFocus: true,
  cadence: (data: AdminConfigWireResponse | undefined) => {
    const status = data?.state.status;
    return status === 'pending' || status === 'preparing' || status === 'applying'
      ? CADENCE.urgent
      : CADENCE.idle;
  },
  request: (_: void, signal) => api.get<AdminConfigWireResponse>('/admin/config', { signal }),
});

/**
 * What SMART's own Claude calls cost (`GET /admin/llm/usage`): the Spend panel
 * above Admin → Config → AI/LLM. Admin-only, like provider cost.
 *
 * Volatile: a call is recorded by a background job, and no data revision moves
 * for it. A minute of freshness spares a request per tab switch inside the
 * section; focus refetch picks up a Daily Briefing written meanwhile. Nothing
 * polls it: a spend readout is read, not watched.
 */
export const adminLlmUsage = defineQuery({
  namespace: 'authoritative',
  name: 'admin.llmUsage',
  volatile: true,
  keyOf: () => [],
  freshness: FRESHNESS.minute,
  refetchOnWindowFocus: true,
  request: (_: void, signal) => api.get<LlmUsageResponse>('/admin/llm/usage', { signal }),
});

/** Cold archive listing (R2 NDJSON). No polling — objects arrive hourly. */
export const adminLogArchive = defineQuery({
  namespace: 'realtime',
  name: 'admin.archiveList',
  volatile: true,
  keyOf: (input: { date: string; hour: string | null }) => [input.date, input.hour],
  freshness: FRESHNESS.minute,
  request: (input: { date: string; hour: string | null }, signal) =>
    api.get<LogArchiveResponse>(
      `/admin/log-archive?date=${input.date}${input.hour ? `&hour=${input.hour}` : ''}`,
      { signal },
    ),
});

/** A single archived object. Content-addressed by key — refetching cannot change it. */
export const adminLogArchiveObject = defineQuery({
  namespace: 'realtime',
  name: 'admin.archiveObject',
  immutable: true,
  keyOf: (key: string) => [key],
  request: (key: string, signal) =>
    api.get<LogsPage>(`/admin/log-archive/object?key=${encodeURIComponent(key)}`, { signal }),
});

/**
 * Who is currently stuck.
 *
 * This is live operational state: an old candidate list would offer to repair
 * entities that recovered hours ago. It is also the population
 * `provider_retry.rs` recomputes every minute, so a stale copy is wrong by
 * construction rather than merely old.
 */
export const adminRemediations = defineQuery({
  namespace: 'realtime',
  name: 'admin.remediations',
  volatile: true,
  // Was `FRESHNESS.live`, i.e. `staleTime: 0`. Combined with a blocking `warm`
  // in the route loader that fetches the list twice on every entry — the loader
  // fills the slot, and it is stale before the observer mounts. `seconds` keeps
  // the "observe another actor" intent (an operator expects a repair they just
  // applied to show up) and costs one request instead of two.
  freshness: FRESHNESS.seconds,
  request: (_: void, signal) =>
    api.get<RemediationCatalogResponse>('/admin/remediations', { signal }),
});

/**
 * What the drift sweep found on published Meta and TTD objects.
 *
 * The payload carries provider configuration, not personal data, but these rows
 * change *underneath the reader*. The sweep flips `repaired_at` on a row it
 * already wrote, so old data could show drift as unfixed minutes after repair.
 * A wrong answer that looks authoritative is worse here than a slower one.
 *
 * No cadence. The window is relative to *now*, so every poll would silently
 * re-scan a shifted window for rows a batch sweep deposits minutes apart;
 * `realtime`'s `staleTime: 0` already re-reads on every arrival at the screen,
 * which is what an operator returning to it wants.
 *
 * No `unwrap`: `truncated` and `window_ms` are not envelope, they are the
 * caveats the rows have to be read with.
 */
/**
 * The volatile classification is justified in the block above.
 *
 * The freshness and cadence are the interesting part. Without an explicit
 * `freshness` this inherits `staleTime: 0`, which means the slot is stale the
 * instant `warm` fills it — so entering the screen fetches twice, once in the
 * blocking loader and again the moment the observer mounts. Reproduced against
 * the real query-core: `{"calls":2}`. `FRESHNESS.seconds` collapses that to one
 * without a timer, exactly as the rules and channels reads above already do.
 *
 * The cadence matters more. This is the drift sweep's only surface, the sweep
 * runs on a three-hour interval and repairs land asynchronously, and a `realtime`
 * resource has `refetchOnWindowFocus: false` — so a screen left open showed the
 * state at the moment it was opened, forever. `CADENCE.slow` is well under the
 * scan interval and costs one small indexed read per 30s while somebody is
 * actually looking at it.
 */
export const adminProviderDrift = defineQuery({
  namespace: 'realtime',
  name: 'admin.providerDrift',
  volatile: true,
  freshness: FRESHNESS.seconds,
  cadence: CADENCE.slow,
  request: (q: ProviderDriftQuery, signal) =>
    api.get<ProviderDriftResponse>(`/admin/provider-drift${buildQs(q)}`, { signal }),
});
